// LicenseRequestDetail/downloadVerificationLetterPdf.js
import { jsPDF } from "jspdf"

/** Small helper to load images as base64 safely */
const loadImageAsDataUrl = (url) =>
  new Promise((resolve) => {
    if (!url) return resolve(null)
    const img = new Image()
    img.crossOrigin = "Anonymous"
    img.onload = () => {
      const canvas = document.createElement("canvas")
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext("2d")
      ctx.drawImage(img, 0, 0)
      resolve(canvas.toDataURL("image/png"))
    }
    img.onerror = () => resolve(null)
    img.src = url
  })

  /**
 * Draw text with a bold substring (fullName) inside a wrapped paragraph.
 */
/**
 * Draw a paragraph with mixed bold and normal text.
 * Uses a simple markup: **bold text**
 */
function drawRichParagraph(doc, rawText, x, y, maxWidth, lineHeight = 14) {
  const segments = [];
  const boldRegex = /\*\*(.*?)\*\*/g;

  let lastIndex = 0;
  let match;

  // Extract bold + normal segments
  while ((match = boldRegex.exec(rawText)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ text: rawText.slice(lastIndex, match.index), bold: false });
    }
    segments.push({ text: match[1], bold: true });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < rawText.length) {
    segments.push({ text: rawText.slice(lastIndex), bold: false });
  }

  let cursorX = x;
  let cursorY = y;

  segments.forEach(seg => {
    const words = seg.text.split(/(\s+)/); // preserve spaces

    words.forEach(word => {
      if (!word) return;

      doc.setFont("times", seg.bold ? "bold" : "normal");

      const wordWidth = doc.getTextWidth(word);

      // If exceeds line, move to next line
      if (cursorX + wordWidth > x + maxWidth) {
        cursorY += lineHeight;
        cursorX = x;
      }

      doc.text(word, cursorX, cursorY);
      cursorX += wordWidth;
    });
  });

  return cursorY + lineHeight;
}

export default async function downloadVerificationLetterPDF({
  grn,
  fullName,
  licenseNumber,
  headerImageUrl,
  signatureImageUrl,

  // LEFT COLUMN ICONS (supply from your backend)
  emailIconUrl,
  phoneIconUrl,
  locationIconUrl,
}) {
  const doc = new jsPDF("p", "pt", "a4")

  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()

  const leftColWidth = 200     // EXACT column width like screenshot
  const rightColX = leftColWidth + 40
  const rightColWidth = pageWidth - rightColX - 40

  let leftY = 150
  let rightY = 150

  /**  LOAD ALL IMAGES  */
  const headerImg = await loadImageAsDataUrl(headerImageUrl)
  const signatureImg = await loadImageAsDataUrl(signatureImageUrl)
  const emailIcon = await loadImageAsDataUrl(emailIconUrl)
  const phoneIcon = await loadImageAsDataUrl(phoneIconUrl)
  const locationIcon = await loadImageAsDataUrl(locationIconUrl)

  // ---------------------------------------------------------
  // LEFT COLUMN – EXACT VISUAL MATCH WITH SAMPLE
  // ---------------------------------------------------------
   

    doc.setFont("times", "bold")
    doc.setFontSize(16)
    doc.text("AUTOMOBILE", 40, leftY)
    leftY += 22
    doc.text("ASSOCIATION:", 40, leftY)
    leftY += 22
    doc.text("INTERNATIONAL", 40, leftY)
    leftY += 22
    doc.text("TRAFFIC CONTROL", 40, leftY)
    leftY += 22
    doc.text("ASSOCIATION", 40, leftY)
    leftY += 40

  // green divider line
  doc.setDrawColor(139, 199, 165)
  doc.setLineWidth(2)
  doc.line(40, leftY, leftColWidth, leftY)
  leftY += 30

  // EMAIL ICON + TEXT
  if (emailIcon) doc.addImage(emailIcon, "PNG", 40, leftY - 12, 22, 22)
  doc.setFont("times", "normal")
  doc.setFontSize(10)
  doc.text("INFO@E-ITCA.ORG", 70, leftY + 5)
  leftY += 40

  // PHONE ICON + TEXT
  if (phoneIcon) doc.addImage(phoneIcon, "PNG", 40, leftY - 12, 22, 22)
  doc.text("+44 7572767278", 70, leftY + 5)
  leftY += 40

  // LOCATION ICON + ADDRESS
  if (locationIcon) doc.addImage(locationIcon, "PNG", 40, leftY - 12, 22, 22)
  const addressLines = doc.splitTextToSize(
    "London: 71–75 Shelton Street,\nCovent Garden, WC2H 9JQ, UK",
    leftColWidth - 50
  )
  doc.text(addressLines, 70, leftY)
  leftY += addressLines.length * 14 + 20

  // green divider
  doc.line(40, leftY, leftColWidth, leftY)
  leftY += 30

  // SIGNATURE (left column bottom)
  if (signatureImg) {
    doc.addImage(signatureImg, "PNG", 40, leftY, 140, 140)
    leftY += 100
  }

  // ---------------------------------------------------------
  // RIGHT COLUMN – EXACT TEXT LAYOUT FROM SAMPLE
  // ---------------------------------------------------------

  // HEADER IMAGE (center top)
  if (headerImg) {
    const headerW = 700
    const headerX = 0
    doc.addImage(headerImg, "PNG", headerX, 20, headerW, 60)
  }

  // Titles
  doc.setFont("times", "bold")
  doc.setFontSize(10)
  doc.text(
    "VERIFICATION OF INTERNATIONAL DRIVING PERMIT (IDP)",
    rightColX,
    rightY,
    { maxWidth: rightColWidth }
  )
  rightY += 30

   doc.setFont("times", "normal")
  doc.setFontSize(10)
  doc.text("TO WHOM IT MAY CONCERN,", rightColX, rightY)
  rightY += 35

  doc.setFont("times", "bold")
  doc.text(
    "IN ACCORDANCE WITH THE UN CONVENTIONS ON ROAD TRAFFIC (1949 & 1968)",
    rightColX,
    rightY,
    { maxWidth: rightColWidth }
  )
  rightY += 45

  // Body (bold fields)
const p1 = 
  `This letter serves to confirm that the bearer of this document, ` +
  `**${fullName}**, holder of national driving license number ` +
  `**${licenseNumber}**, has been issued an ` +
  `**International Driving Permit (IDP)** in accordance with the provisions ` +
  `of the **United Nations Convention on Road Traffic**, either the ` +
  `**1968 Vienna Convention**.`

rightY = drawRichParagraph(doc, p1, rightColX, rightY, rightColWidth, 14);
rightY += 20;

const p2 = `The IDP is a **globally recognized** document that translates the holder’s valid domestic driving license into multiple languages and permits the holder to operate private motor vehicles in foreign countries that are contracting parties to the respective UN Convention, provided that the national license remains valid.
`;
rightY = drawRichParagraph(doc, p2, rightColX, rightY, rightColWidth, 14);
rightY += 20;

  doc.setFont("times", "bold")
  doc.text("This International Driving Permit is:", rightColX, rightY)
  rightY += 18

  // Bullet list
// Bullet list using rich text
const bullets = [
  "• Issued by an authority duly authorized under national law to issue IDPs;",
  "• Valid for use in contracting countries of the relevant Convention;",
  "• Non-renewable and valid for up to **1 year** (1949 Convention) or up to **3 years** (1968 Convention), subject to the validity of the domestic license;",
  "• Not a standalone document – it must be accompanied by a valid domestic driving license at all times."
];

bullets.forEach(b => {
  rightY = drawRichParagraph(doc, b, rightColX, rightY, rightColWidth, 14);
  rightY += 5; // small spacing between bullets
});

rightY += 12; // spacing after bullet section

rightY = drawRichParagraph(
  doc,
  `We confirm that this IDP is in **full compliance** with international road traffic standards and should be accepted as a valid form of driving authorization in participating countries.`,
  rightColX,
  rightY,
  rightColWidth,
  15 // line height
);

rightY += 20; // spacing after p3

  doc.text("Sincerely,", rightColX, rightY)
  rightY += 28

  doc.setFont("times", "bold")
  doc.text(
    "Automobile Association: International traffic control association",
    rightColX,
    rightY
  )

  // SAVE FILE
  const fileName = `${grn || "verification-letter"}.pdf`
  doc.save(fileName)
}